import { NoteService } from '../services/note.service';

/**
 * Handles saving a new note
 * @param noteIndex The index of the note in the content data
 * @param contentData The current content data
 * @param selectedChapterId The currently selected chapter ID
 * @param setItemSubmitting State setter for item submitting status
 * @param setContentData State setter for content data
 * @param setEditingNoteIndex State setter for editing note index
 */
export const saveNewNote = async (
  noteIndex: number,
  contentData: any,
  selectedChapterId: string,
  setItemSubmitting: React.Dispatch<React.SetStateAction<{type: string, index: number, subIndex?: number} | null>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditingNoteIndex: React.Dispatch<React.SetStateAction<number | null>>
): Promise<void> => {
  if (!contentData) return;
  if (!selectedChapterId) {
    alert('No chapter selected');
    return;
  }
  
  const note = contentData.notes[noteIndex];
  const chapterId = parseInt(selectedChapterId, 10);
  
  try {
    setItemSubmitting({ type: 'note', index: noteIndex });
    
    // Always include display_order if available
    const noteData: {
      content: string,
      chapter_id: number,
      display_order?: number
    } = {
      content: note.content.trim(),
      chapter_id: chapterId
    };

    // Include display_order in API request if it exists in our local state
    if (note.display_order !== undefined) {
      noteData.display_order = note.display_order;
    }
    
    const newNote = await NoteService.createNote(noteData);
    
    // Update content data, preserve questions but use other properties from API response
    const updatedNotes = [...contentData.notes];
    updatedNotes[noteIndex] = {
      ...newNote,
      questions: note.questions || []
    };
    
    setContentData({
      ...contentData,
      notes: updatedNotes
    });
    
    setEditingNoteIndex(null);
  } catch (err) {
    console.error('Error creating note:', err);
    alert('Failed to create note. Please try again later.');
  } finally {
    setItemSubmitting(null);
  }
};

/**
 * Handles updating an existing note
 * @param noteIndex The index of the note in the content data
 * @param contentData The current content data
 * @param isNewContent Flag indicating if this is new content
 * @param setItemSubmitting State setter for item submitting status
 * @param setContentData State setter for content data
 * @param setEditingNoteIndex State setter for editing note index
 */
export const updateExistingNote = async (
  noteIndex: number,
  contentData: any,
  isNewContent: boolean,
  setItemSubmitting: React.Dispatch<React.SetStateAction<{type: string, index: number, subIndex?: number} | null>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditingNoteIndex: React.Dispatch<React.SetStateAction<number | null>>,
  saveNewNoteFn: (noteIndex: number) => Promise<void>
): Promise<void> => {
  if (!contentData) return;
  
  const note = contentData.notes[noteIndex];
  
  if (!note.content || note.content.trim() === '') {
    alert('Note content cannot be empty');
    return;
  }
  
  if (isNewContent) {
    // For new content, just update local state
    setEditingNoteIndex(null);
  } else {
    // For existing note, update in the database
    if (!note.id) {
      // New note needs to be created
      await saveNewNoteFn(noteIndex);
    } else {
      // Existing note needs to be updated
      try {
        setItemSubmitting({ type: 'note', index: noteIndex });
        
        // Include display_order in update if it exists
        const updateData: {
          content: string,
          display_order?: number
        } = {
          content: note.content.trim()
        };
        
        if (note.display_order !== undefined) {
          updateData.display_order = note.display_order;
        }
        
        const updatedNote = await NoteService.updateNote(note.id, updateData);
        
        // Update content data, preserving questions but using other properties from API response
        const updatedNotes = [...contentData.notes];
        updatedNotes[noteIndex] = {
          ...updatedNotes[noteIndex], // Keep existing properties
          ...updatedNote,             // Update with API response values
          questions: updatedNotes[noteIndex].questions // Preserve questions
        };
        
        setContentData({
          ...contentData,
          notes: updatedNotes
        });
        
        setEditingNoteIndex(null);
      } catch (err) {
        console.error('Error updating note:', err);
        alert('Failed to update note. Please try again later.');
      } finally {
        setItemSubmitting(null);
      }
    }
  }
};

/**
 * Handles deleting a note
 * @param noteIndex The index of the note to delete
 * @param contentData The current content data
 * @param isNewContent Flag indicating if this is new content
 * @param setItemSubmitting State setter for item submitting status
 * @param setContentData State setter for content data
 */
export const deleteNote = async (
  noteIndex: number,
  contentData: any,
  isNewContent: boolean,
  setItemSubmitting: React.Dispatch<React.SetStateAction<{type: string, index: number, subIndex?: number} | null>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>
): Promise<void> => {
  if (!contentData) return;
  
  const note = contentData.notes[noteIndex];
  
  // For new content (JSON processing), just update local state without confirmation
  if (isNewContent) {
    const updatedNotes = [...contentData.notes];
    updatedNotes.splice(noteIndex, 1);
    
    setContentData({
      ...contentData,
      notes: updatedNotes
    });
    return;
  }
  
  // For existing note, confirm before deleting from database
  if (window.confirm('Are you sure you want to delete this note and all its questions?')) {
    if (note.id) {
      try {
        setItemSubmitting({ type: 'note', index: noteIndex });
        
        await NoteService.deleteNote(note.id);
        
        // Update content data
        const updatedNotes = [...contentData.notes];
        updatedNotes.splice(noteIndex, 1);
        
        setContentData({
          ...contentData,
          notes: updatedNotes
        });
      } catch (err) {
        console.error('Error deleting note:', err);
        alert('Failed to delete note. Please try again later.');
      } finally {
        setItemSubmitting(null);
      }
    } else {
      // New note that hasn't been saved yet - no need for confirmation
      const updatedNotes = [...contentData.notes];
      updatedNotes.splice(noteIndex, 1);
      
      setContentData({
        ...contentData,
        notes: updatedNotes
      });
    }
  }
};

/**
 * Adds a new note at a specific position
 * @param position The position to add the note at ('start', 'end', or an index)
 * @param contentData The current content data
 * @param setContentData State setter for content data
 * @param setEditingNoteIndex State setter for editing note index
 */
export const addNoteAtPosition = (
  position: 'start' | 'end' | number,
  contentData: any,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditingNoteIndex: React.Dispatch<React.SetStateAction<number | null>>
): void => {
  if (!contentData) return;
  
  const updatedNotes = [...contentData.notes];
  let newNoteIndex: number;
  let displayOrder: number | undefined;
  
  // Default base display order if none exists
  const baseDisplayOrder = 1000;
  const displayOrderStep = 1000;
  
  // Calculate the appropriate display_order based on position
  if (position === 'start') {
    // Add at the beginning
    newNoteIndex = 0;
    
    if (updatedNotes.length > 0) {
      // If first note has display_order, use half of its value
      // Otherwise use a default value less than what would be typical
      const firstNoteOrder = updatedNotes[0].display_order !== undefined 
        ? updatedNotes[0].display_order 
        : baseDisplayOrder;
      
      displayOrder = Math.max(1, Math.floor(firstNoteOrder / 2));
    } else {
      // If no notes exist, use base value
      displayOrder = baseDisplayOrder;
    }
  } else if (position === 'end') {
    // Add at the end
    newNoteIndex = updatedNotes.length;
    
    if (updatedNotes.length > 0) {
      // If last note has display_order, add step to it
      // Otherwise use a default value higher than typical
      const lastNoteOrder = updatedNotes[updatedNotes.length - 1].display_order !== undefined 
        ? updatedNotes[updatedNotes.length - 1].display_order 
        : baseDisplayOrder * updatedNotes.length;
      
      displayOrder = lastNoteOrder + displayOrderStep;
    } else {
      // If no notes exist, use base value
      displayOrder = baseDisplayOrder;
    }
  } else {
    // Add between notes
    newNoteIndex = position;
    
    if (updatedNotes.length > 0) {
      if (position === 0) {
        // Adding at position 0 but notes exist (same as 'start')
        const firstNoteOrder = updatedNotes[0].display_order !== undefined 
          ? updatedNotes[0].display_order 
          : baseDisplayOrder;
        
        displayOrder = Math.max(1, Math.floor(firstNoteOrder / 2));
      } else if (position >= updatedNotes.length) {
        // Adding beyond the end (same as 'end')
        const lastNoteOrder = updatedNotes[updatedNotes.length - 1].display_order !== undefined 
          ? updatedNotes[updatedNotes.length - 1].display_order 
          : baseDisplayOrder * updatedNotes.length;
        
        displayOrder = lastNoteOrder + displayOrderStep;
      } else {
        // Adding between two notes - calculate the middle value
        const prevNote = updatedNotes[position - 1];
        const nextNote = updatedNotes[position];
        
        const prevNoteOrder = prevNote.display_order !== undefined 
          ? prevNote.display_order 
          : baseDisplayOrder * (position - 1);
          
        const nextNoteOrder = nextNote.display_order !== undefined 
          ? nextNote.display_order 
          : baseDisplayOrder * position;
        
        // Calculate the middle value between the two display orders
        displayOrder = Math.floor((prevNoteOrder + nextNoteOrder) / 2);
        
        // If by rare chance the orders are consecutive, use a decimal value
        if (displayOrder === prevNoteOrder) {
          displayOrder = prevNoteOrder + 1;
        }
      }
    } else {
      // If no notes exist, use base value
      displayOrder = baseDisplayOrder;
    }
  }
  
  // Create the new note with calculated display_order
  const newNote = {
    content: '',
    display_order: displayOrder,
    questions: []
  };
  
  // Insert the new note at the calculated position
  updatedNotes.splice(newNoteIndex, 0, newNote);
  
  setContentData({
    ...contentData,
    notes: updatedNotes
  });
  
  // Start editing the new note
  setEditingNoteIndex(newNoteIndex);
};